import { useMutation, useQuery } from "@apollo/client";
import * as React from "react";
import {
  CreateHotWaterSchedules,
  CreateHotWaterSchedulesMutation,
  CreateHotWaterSchedulesMutationVariables,
  GetHotWaterSchedules,
  GetHotWaterSchedulesQuery,
  GetHotWaterSchedulesQueryVariables,
  HotWaterScheduleInputs,
  HotWaterScheduleResponse,
  HotWaterScheduleSetpointInputs,
  HotWaterScheduleSetpointResponse,
  HotWaterSchedulesResponse,
  WeeklyHotWaterScheduleInputs,
} from "../../../shared/api-clients/gql";
import { IScheduleArray, IZoneArray } from "../helpers/scheduleHelper";
import { ScheduleMutator } from "./Schedules";

export interface IHotWaterSchedulesContext extends ScheduleMutator {
  hotWaterSchedules: HotWaterSchedulesResponse;
  hotwaterMutationInProgress: boolean;
  addMultipleSchedules: (newSchedules: IMultiSchedule) => Promise<void>;
  editMultipleSchedules: (zones: IZoneArray) => Promise<void>;
}

export interface IMultiSchedule {
  startTime: string;
  days: string[];
}

const createEmptySchedule = (): WeeklyHotWaterScheduleInputs => ({
  monday: [{ zone_id: "hot_water", setpoints: [] }],
  tuesday: [{ zone_id: "hot_water", setpoints: [] }],
  wednesday: [{ zone_id: "hot_water", setpoints: [] }],
  thursday: [{ zone_id: "hot_water", setpoints: [] }],
  friday: [{ zone_id: "hot_water", setpoints: [] }],
  saturday: [{ zone_id: "hot_water", setpoints: [] }],
  sunday: [{ zone_id: "hot_water", setpoints: [] }],
});

export const HotWaterSchedulesContext =
  React.createContext<IHotWaterSchedulesContext>(undefined);

export const HotWaterSchedulesProvider = ({
  children,
  deviceId,
}: React.PropsWithChildren<{ deviceId: string }>) => {
  const { data: hotWaterSchedules } = useQuery<
    GetHotWaterSchedulesQuery,
    GetHotWaterSchedulesQueryVariables
  >(GetHotWaterSchedules, {
    fetchPolicy: "network-only",
    pollInterval: 5000,
    variables: {
      deviceId,
    },
  });

  const flattenSetpoints = (
    allSetpoints: HotWaterScheduleResponse[]
  ): HotWaterScheduleInputs => ({
    zone_id: "hot_water",
    setpoints: allSetpoints
      ? allSetpoints?.flatMap<HotWaterScheduleSetpointInputs>((setpoint) =>
          setpoint.setpoints
            ?.map<HotWaterScheduleSetpointInputs>((actualSetpoint) => ({
              hot_water_active: actualSetpoint.hot_water_active,
              time: actualSetpoint.time,
              user_time: actualSetpoint.user_time,
            }))
            .filter((schedule) => schedule.user_time)
        )
      : [],
  });

  const createWeeklyHotWaterScheduleInputs = () =>
    Object.entries(hotWaterSchedules?.getHotWaterSchedules)?.reduce(
      (acc, [key, value]) => {
        acc[key] = [flattenSetpoints(value)];
        return acc;
      },
      {}
    );

  const [createSchedule, { loading: hotwaterMutationInProgress }] = useMutation<
    CreateHotWaterSchedulesMutation,
    CreateHotWaterSchedulesMutationVariables
  >(CreateHotWaterSchedules, {
    refetchQueries: [{ query: GetHotWaterSchedules, variables: { deviceId } }],
  });

  const createHotWaterSchedule = async (
    days: string[],
    setpoint: HotWaterScheduleSetpointResponse
  ) => {
    const hws: WeeklyHotWaterScheduleInputs =
      createWeeklyHotWaterScheduleInputs();
    days.forEach(async (currentDay) => {
      hws[currentDay][0].setpoints = [
        //   eslint-disable-next-line
        ...hws[currentDay]?.[0].setpoints,
        {
          hot_water_active: setpoint.hot_water_active,
          time: setpoint.time,
          user_time: setpoint.user_time,
        },
        // Sort to ensure the setpoints are in the correct order
      ].sort((a, b) => a.user_time.localeCompare(b.user_time));
    });
    await createSchedule({
      variables: {
        deviceId,
        hotWaterSchedules: hws,
      },
    });
  };

  const addMultipleSchedules = async (newSchedules: IMultiSchedule) =>
    createHotWaterSchedule(newSchedules.days, {
      user_time: newSchedules.startTime,
      time: newSchedules.startTime,
      hot_water_active: 1,
    });

  const editMultipleSchedules = async (schedules: IZoneArray) => {
    const hws = createEmptySchedule();
    // Convert schedules.hot_water to a WeeklyHotWaterScheduleInputs
    schedules.hot_water.forEach((schedule: IScheduleArray) => {
      // For each day in each schedule add the setpoint and if it doesn't exist create the day
      schedule.day.forEach((d: keyof WeeklyHotWaterScheduleInputs) => {
        hws[d][0].setpoints.push({
          user_time: schedule.start,
          time: schedule.start,
          hot_water_active: 1,
        });
      });
    });

    await createSchedule({
      variables: {
        deviceId,
        hotWaterSchedules: hws,
      },
    });
  };

  return (
    <HotWaterSchedulesContext.Provider
      value={{
        hotWaterSchedules: hotWaterSchedules?.getHotWaterSchedules,
        hotwaterMutationInProgress,
        addMultipleSchedules,
        editMultipleSchedules,
      }}
    >
      {children}
    </HotWaterSchedulesContext.Provider>
  );
};

export const useHotWaterSchedules = () =>
  React.useContext(HotWaterSchedulesContext);
